import React, { useCallback, useEffect, useRef, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { AnimatePresence, motion } from 'framer-motion';
import { LuPhoneMissed } from 'react-icons/lu';
import { auth, db } from '../../config/firebaseConfig';
import { useVideoCall } from '../Teleconference/VideoCallContext';
import {
  MISSED_CALL_HISTORY_MS,
  markMissedCallSeen,
  toDate,
} from '../../utils/missedCalls';
import { normalizeStoredName } from '../../utils/userIdentity';
import IconButton from '../ui/IconButton';
import { useTranslation } from '../../i18n/LanguageContext';
import MissedCallsPanel from './MissedCallsPanel';

/**
 * Üst çubuktaki cevapsız arama bildirimi.
 *
 * Kaynak, `callRooms` koleksiyonunun kendisidir: kullanıcıya gelen ve
 * 'missed' durumuna düşmüş, henüz okunmamış odalar listelenir (bkz.
 * utils/missedCalls). Okundu işaretlenen kayıt listeden düşer.
 */
const MissedCallsBell = () => {
  const [missedCalls, setMissedCalls] = useState([]);
  const [open, setOpen] = useState(false);
  const [authEmail, setAuthEmail] = useState(auth.currentUser?.email || null);
  const panelRef = useRef(null);
  const { initiateVideoCall, initiateVoiceCall } = useVideoCall();
  const { t } = useTranslation();

  useEffect(() => onAuthStateChanged(auth, (user) => setAuthEmail(user?.email || null)), []);

  useEffect(() => {
    if (!authEmail) {
      setMissedCalls([]);
      return undefined;
    }

    const q = query(
      collection(db, 'callRooms'),
      where('inviteeEmail', '==', authEmail),
      where('status', '==', 'missed')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const cutoff = Date.now() - MISSED_CALL_HISTORY_MS;
        const rooms = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          // Okunmuş ve çok eski kayıtlar listeyi doldurmasın
          .filter((room) => !room.missedSeenAt)
          .filter((room) => {
            const at = toDate(room.missedAt || room.createdAt);
            return !at || at.getTime() >= cutoff;
          })
          .sort((a, b) => {
            const aAt = toDate(a.missedAt || a.createdAt)?.getTime() || 0;
            const bAt = toDate(b.missedAt || b.createdAt)?.getTime() || 0;
            return bAt - aAt;
          });
        setMissedCalls(rooms);
      },
      (error) => console.debug('Cevapsız aramalar okunamadı:', error)
    );

    return () => unsubscribe();
  }, [authEmail]);

  // Panel dışına tıklayınca kapansın
  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const dismiss = useCallback(async (room) => {
    await markMissedCallSeen(room.roomId || room.id);
  }, []);

  const dismissAll = useCallback(async () => {
    await Promise.all(missedCalls.map((room) => markMissedCallSeen(room.roomId || room.id)));
    setOpen(false);
  }, [missedCalls]);

  const callBack = useCallback(
    async (room, type) => {
      await dismiss(room);
      setOpen(false);
      const start = type === 'voice' ? initiateVoiceCall : initiateVideoCall;
      await start(
        room.createdBy,
        normalizeStoredName(room.createdByName, room.createdBy),
        null
      );
    },
    [dismiss, initiateVideoCall, initiateVoiceCall]
  );

  if (missedCalls.length === 0) return null;

  return (
    <div ref={panelRef} className="tw-relative">
      <IconButton
        label={t('missedCalls.button')}
        badge={missedCalls.length}
        tone="alert"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        <LuPhoneMissed aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
      </IconButton>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.97 }}
            transition={{ duration: 0.16 }}
            className="tw-absolute tw-right-0 tw-top-[calc(100%+0.6rem)] tw-z-20 tw-w-[22rem] tw-max-w-[calc(100vw-1.5rem)] tw-origin-top-right"
          >
            <MissedCallsPanel
              missedCalls={missedCalls}
              onCallBack={callBack}
              onDismiss={dismiss}
              onDismissAll={dismissAll}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MissedCallsBell;
